import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { noSaveVideo } from './noSave';
import { Watermark } from './Watermark';

/**
 * A child's video, playing in its place, whose fullscreen keeps the viewer watermark (Q40, Q49).
 *
 * The browser's own fullscreen puts the bare `<video>` on the screen and leaves every layer of the page
 * behind, the watermark included. So the player's fullscreen is switched off (`noSaveVideo`) and this button
 * puts the wrapper into fullscreen instead, with the video and a watermark of its own inside it. Where the
 * browser cannot put an element other than a video into fullscreen (Safari on an iPhone), there is no button
 * and the video plays inline (`playsInline`), under the screen's watermark.
 */
export function ProtectedVideo({ src, type, label, className }: {
  src: string;
  type?: string | null;
  label?: string;
  className?: string;
}) {
  const { t } = useI18n();
  const wrapper = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === wrapper.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggle = () => {
    // A refusal (no user gesture, a policy) leaves the video where it is, which is all anyone can do about it.
    const done = fullscreen ? document.exitFullscreen() : wrapper.current?.requestFullscreen();
    void done?.catch(() => undefined);
  };

  return (
    <div ref={wrapper} className="dg-video">
      <video {...noSaveVideo} className={className} controls playsInline preload="metadata" aria-label={label}>
        {/* iOS Safari is reported to leave a "metadata" tile blank until play. Asking for a frame just after
            the start ("#t=0.1") is the usual way to make it draw one. The fragment never reaches the server. */}
        <source src={`${src}#t=0.1`} type={type ?? undefined} />
      </video>
      {document.fullscreenEnabled && (
        <button type="button" className="dg-button dg-button--secondary dg-video__fullscreen" onClick={toggle}>
          {t(fullscreen ? 'media.fullscreen.exit' : 'media.fullscreen')}
        </button>
      )}
      {/* Only in fullscreen: on the page, the screen's own watermark already covers it. */}
      {fullscreen && <Watermark />}
    </div>
  );
}
